import React from "react";
import { motion } from "framer-motion";

/* ═══════════════════════════════════════════════════════════════
   People — the humans behind the pipeline.
   The VP's brief: "one section that conveys the people behind the
   pipeline". Placeholder-friendly — swap avatars & names when the
   client sends them.
   ═══════════════════════════════════════════════════════════════ */

const TEAM = [
  {
    initials: "VW",
    name: "Founder Redacted",
    role: "Founder · CEO",
    yrs: "22 yrs",
    quote: "You can quote a lower number. You can't fake the crew that stands behind it.",
    location: "Mumbai",
  },
  {
    initials: "EP",
    name: "Executive Producer",
    role: "Head of Production",
    yrs: "18 yrs",
    quote: "My job is to make sure your buyer's Monday looks exactly like your Friday review.",
    location: "Los Angeles",
  },
  {
    initials: "AS",
    name: "Animation Supervisor",
    role: "Head of Animation",
    yrs: "16 yrs",
    quote: "Weight, timing, acting. Everything else is decoration.",
    location: "Mumbai",
  },
  {
    initials: "VS",
    name: "VFX Supervisor",
    role: "Head of FX · Look-Dev",
    yrs: "14 yrs",
    quote: "We ship shots, not screenshots.",
    location: "Mumbai",
  },
  {
    initials: "SD",
    name: "Story Head",
    role: "Head of Story · Boards",
    yrs: "20 yrs",
    quote: "If the board doesn't work in black and white, no rendering will save it.",
    location: "Copenhagen",
  },
  {
    initials: "LP",
    name: "Line Producer",
    role: "Line Producer · North America",
    yrs: "12 yrs",
    quote: "I answer three questions: how much, how long, and what could go wrong.",
    location: "Vancouver",
  },
];

export default function People() {
  return (
    <section
      id="people"
      className="vw-section relative border-t border-[var(--vw-border)] bg-[var(--vw-bg-soft)]"
      data-testid="vw-people"
    >
      <div className="vw-container">
        {/* Header */}
        <div className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-3xl">
            <div className="vw-eyebrow mb-6">PEOPLE BEHIND THE PIPELINE</div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight">
              The pipeline doesn't ship the film. <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">These people do.</span>
            </h2>
          </div>
          <p className="max-w-md text-[var(--vw-text-dim)] leading-relaxed">
            Six leads at the top of the room. 150+ artists on the floor. Every one of them has a name, a shot list, and a phone number your line producer can call.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--vw-border)]">
          {TEAM.map((p, i) => (
            <motion.figure
              key={p.name + i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
              className="group relative bg-[var(--vw-bg-soft)] p-7 md:p-8 hover:bg-[var(--vw-surface)] transition-colors min-h-[300px] flex flex-col"
              data-testid={`vw-person-${i}`}
            >
              {/* Avatar block — placeholder-friendly initials tile */}
              <div className="flex items-start gap-4 mb-6">
                <div className="relative w-16 h-16 border border-[var(--vw-cyan)]/40 grid place-items-center bg-black/40 overflow-hidden shrink-0">
                  {/* Ghost portrait geometry */}
                  <svg viewBox="0 0 60 60" className="absolute inset-0 w-full h-full opacity-[0.14]" aria-hidden>
                    <defs>
                      <linearGradient id={`pgrad${i}`} x1="0" y1="0" x2="60" y2="60" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#00E5FF"/>
                        <stop offset="100%" stopColor="#2B6CFF"/>
                      </linearGradient>
                    </defs>
                    <circle cx="30" cy="22" r="10" fill="none" stroke={`url(#pgrad${i})`} strokeWidth="0.8"/>
                    <path d="M10 58 Q30 40 50 58" fill="none" stroke={`url(#pgrad${i})`} strokeWidth="0.8"/>
                  </svg>
                  <span className="relative font-display text-xl text-[var(--vw-cyan)] tracking-tight">
                    {p.initials}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-[19px] md:text-[20px] leading-tight text-[var(--vw-text)] tracking-tight mb-1 truncate">
                    {p.name}
                  </div>
                  <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[var(--vw-cyan)] mb-1">
                    {p.role}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] flex items-center gap-2">
                    <span>{p.yrs}</span>
                    <span className="w-1 h-1 rounded-full bg-[var(--vw-text-mute)]/50" />
                    <span>{p.location}</span>
                  </div>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="font-serif-italic text-[17px] md:text-[18px] leading-[1.35] text-[var(--vw-text-dim)] mt-auto">
                “{p.quote}”
              </blockquote>

              {/* Hover accent */}
              <span className="absolute bottom-0 left-0 h-px w-0 bg-[var(--vw-cyan)] group-hover:w-full transition-all duration-500" />
            </motion.figure>
          ))}
        </div>

        {/* Footer strip */}
        <div className="mt-12 md:mt-14 flex flex-col md:flex-row md:items-center gap-6 md:justify-between border-t border-[var(--vw-border)] pt-8">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] flex items-center gap-3">
            <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />
            Full team roster available under NDA · Ask for the org deck
          </div>
          <a href="#contact" className="vw-btn-ghost vw-focus self-start md:self-auto" data-testid="vw-people-cta">
            Meet the crew on a call
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </a>
        </div>
      </div>
    </section>
  );
}
